import { Box, Typography } from '@mui/material';
import { CaretLeft, List } from 'phosphor-react';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';

import { Sidebar } from '../../Sidebar';
import { SidebarExtensionProvider } from '../../../context/SidebarExtensionProvider/SidebarExtensionProvider';
import { UserService } from '../../../Service/UserService';
import { User } from '../../../Utilis/Types/User';
import { createNotification } from '../../../store/notifications/notification-slice';
import { Loading } from '../../Loading';
import { useAdminLayoutItems } from './useAdminLayoutItems';

enum ClientNavigationTab {
  HOME = 'home',
  DOCUMENTS = 'documents',
  REPORTS = 'reports',
  GENERAL_INFORMATION = 'general-information',
}

const AdminLayout = () => {
  const authUser = useAppSelector(state => state.user.user);
  const { userId } = useParams();
  const location = useLocation();
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();
  const [onOpenSettings, setOnOpenSettings] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState({
    sort: false,
    status: false,
    type: false,
  });
  const [clientDetails, setClientDetails] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { sidebarItems, dropdownItems } = useAdminLayoutItems();

  const pathSegments = useMemo(
    () => location.pathname.split('/').filter(segment => segment !== ''),
    [location.pathname]
  );

  const isClientDetails = useMemo(
    () => pathSegments[1] === 'user',
    [pathSegments]
  );

  const clientNavigationTabs = Object.values(ClientNavigationTab);

  const clientNavigationTab: ClientNavigationTab = useMemo(() => {
    const lastSegment = pathSegments[
      pathSegments.length - 1
    ] as ClientNavigationTab;

    if (clientNavigationTabs.includes(lastSegment)) {
      return lastSegment;
    } else {
      return ClientNavigationTab.HOME;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isClientDetails) {
      fetchClient();
    } else {
      setClientDetails(null);
    }
  }, [isClientDetails]);

  const fetchClient = async () => {
    setIsLoading(true);
    try {
      const response = await UserService.getOne(userId!);

      setClientDetails(response.data);
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong , try again later!',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const header = useMemo(
    () => (
      <Typography
        padding={'32px'}
        fontWeight={590}
        fontSize={'26px'}
        lineHeight={'32px'}
        fontFamily={'Bebas Neue, sans-serif'}
        variant='h5'
        color={'#202A2D'}
      >
        K2
        <Typography fontSize={'26px'} lineHeight={'32px'} variant='caption'>
          Admin
        </Typography>
      </Typography>
    ),
    []
  );

  return (
    <Box
      onClick={() => {
        if (onOpenSettings) {
          setOnOpenSettings(false);
        }

        if (openFilter) {
          if (openFilter.status || openFilter.type || openFilter.sort) {
            setOpenFilter({ type: false, sort: false, status: false });
          } else {
            return;
          }
        }
      }}
    >
      <Box display='flex' height='100vh'>
        <Sidebar
          header={header}
          sidebarItems={sidebarItems}
          closeDrawer={() => setIsDrawerOpen(false)}
          isDrawerOpen={isDrawerOpen}
          dropdownItems={dropdownItems}
          user={authUser}
        />

        <Box display='flex' flexDirection='column' width='100%' height='100%'>
          <Box
            sx={{
              width: '100%',
              overflowX: 'scroll',
              display: 'flex',
              gap: '8px',
              alignItems: 'end',
              paddingTop: '28px',
              borderBottom: '1px solid #DBE0E2',
              ml: '4px',
              '-ms-overflow-style': 'none',
              '::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <Box
              sx={{
                display: 'none',
                '@media (max-width: 768px)': {
                  display: 'block',
                },
              }}
            >
              <List
                cursor={'pointer'}
                onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                size={30}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '28px',
              }}
            >
              {isClientDetails ? (
                <>
                  <CaretLeft
                    size={16}
                    color='#736977'
                    cursor={'pointer'}
                    onClick={() => navigate('/admin')}
                  />
                  {clientNavigationTabs.map(tab => {
                    let path = '/admin/user/' + userId;

                    if (tab !== ClientNavigationTab.HOME) {
                      path = path + `/${tab}`;
                    }

                    return (
                      <Typography
                        key={tab}
                        fontWeight={'500'}
                        fontSize={'20px'}
                        lineHeight={'24px'}
                        borderBottom={
                          clientNavigationTab === tab
                            ? '5px solid #3293DB'
                            : 'none'
                        }
                        sx={{
                          cursor: 'pointer',
                        }}
                        paddingBottom={'6px'}
                        textTransform={'capitalize'}
                        onClick={() => navigate(path)}
                      >
                        {tab}
                      </Typography>
                    );
                  })}
                </>
              ) : (
                <Typography
                  fontWeight={'500'}
                  fontSize={'20px'}
                  lineHeight={'24px'}
                  borderBottom={'5px solid #3293DB'}
                  paddingBottom={'6px'}
                  ml={'48px'}
                  textTransform={'capitalize'}
                >
                  Dashboard
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              width: '100%',
              height: '100%',
              flexDirection: 'row',
              overflowY: 'hidden',
            }}
          >
            <SidebarExtensionProvider>
              <Box
                sx={{
                  width: '100%',
                  overflowX: 'hidden',
                  backgroundColor: '#FAFAFB',
                }}
              >
                <Loading isLoading={isLoading}>
                  <Outlet
                    context={[
                      clientDetails,
                      setClientDetails,
                      setOpenFilter,
                      openFilter,
                    ]}
                  />
                </Loading>
              </Box>
            </SidebarExtensionProvider>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
